import { Flex, layout, styled } from '@fareye/ui'
import {
  FaSignal,
  capitalize,
  hhmmAdoMMMNoIST,
  toTileCase,
  FaBatteryFull,
  FaBatteryEmpty,
  FaBatteryQuarter,
  getValueFromNestedKey,
  FaBatteryThreeQuarters,
  startCase,
  isEmpty,
  pascalCase,
  formatMs
} from '@fareye/utils'
import React from 'react'
import MultiModelStoryLine from '../../../components/MultiModelStoryLine'
import { displayTime } from '../../../components/Timeline/function'
import { Span, Link, BoldText } from '../../../components/Timeline/styled'
import { formatters, Svg } from '../../../config/constants'
import { ColorSpan } from '../../../style/styled'
import { IColumn } from '../../../utility/types'
import CarrierPopover from './Carrier'

const BgSpan = styled(Span)`
  background: ${props => props.theme.colors[props?.title || 'inherit']};
`
interface IHeader {
  align?: 'left' | 'right' | 'center'
}

const Fa = {
  FaBatteryEmpty,
  FaBatteryFull,
  FaBatteryQuarter,
  FaBatteryThreeQuarters
}

const stringtoReactIconName = (name: string) =>
  name
    .split('-')
    .map(item => capitalize(item))
    .join('')

const showFirstWord = (name = '') => name.split(' ')[0]

function getNameOnTransportMode(mode: string) {
  switch (mode) {
    case 'ocean':
      return 'Vessel No.'
    default:
      return 'Vehicle No.'
  }
}
function getColorStatus(color: string) {
  switch (color) {
    case 'DELIVERED':
      return '#2D9A4C'
    case 'UNDELIVERED':
      return '#DA1E28'
    default:
      return '#F1C21B'
  }
}
const Oval = styled.div`
  height: 7px;
  width: 7px;
  background: ${props => props.color};
  border-radius: 50%;
  margin-right: 4px;
`
const ColFullHeight = styled((props: any) => (
  <Flex
    p={2}
    justifyContentSpaceEvenly
    column
    {...props}
    style={{
      height: '100%',
      ...props.style
    }}
  >
    {props.children}
  </Flex>
))`
  ${layout}
`
const FullHeight = styled((props: any) => (
  <Flex
    p={2}
    alignItemsCenter
    textCenter
    {...props}
    style={{ height: '100%', ...props.style }}
  >
    {props.children}
  </Flex>
))`
  ${layout}
`

const Header: any = styled(Span).attrs(() => ({ variant: 'secondary' }))`
  width: 100%;
  text-align: ${({ align = 'left' }: IHeader) => align};
  display: block;
  text-transform: uppercase;
  padding: 7px;
`

function generateColumnObject(
  column: IColumn,
  { t, links, queryParams = {} }: any
) {
  const { label, key, formatter, columnConfig } = column
  switch (key) {
    case 'expand':
      return {
        Header: () => (
          <Header data-id={key} style={{ display: 'none' }}></Header>
        ),
        id: 'expand',
        expander: true,
        width: 0,
        style: { padding: 0, display: 'none' },
        Expander: () => null
      }
    case 'trackingIdList':
      return {
        Header: () => <Header data-id={key}>{t(label)}</Header>,
        id: key,
        width: 150,
        sortable: false,
        sticky: 'left',
        ...columnConfig,
        Cell: ({ row: { original: tr } }: any) => (
          <ColFullHeight>
            {/* <Span variant="ellipsis"> */}
            <Link
              target="_blank"
              variant="xs"
              to={`${links.tripShare}/${
                tr.parcelDigest
              }${queryParams.tripShare || ''}`}
              data-id="trackingId"
            >
              {tr.trackingId}
            </Link>
          </ColFullHeight>
        )
      }
    case 'trackingId':
      return {
        Header: () => <Header data-id={key}>{t(label)}</Header>,
        id: key,
        width: 150,
        sortable: false,
        ...columnConfig,
        Cell: ({ row: { original: tr } }: any) => (
          <ColFullHeight>
            {/* <Span variant="ellipsis"> */}
            <Link
              target="_blank"
              variant="xs"
              to={`${links.tripShare}/${
                tr.parcelDigest
              }${queryParams.tripShare || ''}`}
              data-id="trackingId"
            >
              {tr.trackingId}
            </Link>
          </ColFullHeight>
        )
      }
    case 'parcelRoute':
      return {
        Header: () => (
          <Header data-id={'origin,destination'}>{t(label)}</Header>
        ),
        id: key,
        width: 160,
        sortable: false,
        ...columnConfig,
        Cell: ({ row: { original: tr } }: any) => (
          <ColFullHeight>
            <MultiModelStoryLine
              story={[
                tr.originCity || tr.origin,
                tr.destinationCity || tr.destination
              ]}
            />
          </ColFullHeight>
        )
      }
    case 'gateInOut':
      return {
        Header: () => (
          <Header data-id={'act_start_time_utc,act_end_time_utc'}>
            {t(label)}
          </Header>
        ),
        id: key,
        width: 140,
        sortable: false,
        ...columnConfig,
        Cell: ({ row: { original: tr } }: any) => {
          const startTime =
            formatter && formatters[formatter]
              ? formatters[formatter](tr.actStartTimeUtc) || '-'
              : tr.actStartTimeUtc || (tr.actStartTimeUtc === 0 ? 0 : '-')
          const endTime =
            formatter && formatters[formatter]
              ? formatters[formatter](tr.actEndTimeUtc) || '-'
              : tr.actEndTimeUtc || (tr.actEndTimeUtc === 0 ? 0 : '-')
          return (
            <ColFullHeight>
              <MultiModelStoryLine story={[startTime, endTime]} />
            </ColFullHeight>
          )
        }
      }
    case 'transitTime':
      return {
        Header: () => <Header>{t(label)}</Header>,
        id: key,
        sortable: false,
        width: 150,
        Cell: ({ row: { original: tr } }: any) => {
          return (
            <ColFullHeight>
              <ColorSpan
                variant="h6"
                title={tr?.packageStatus?.replace(' ', '')}
              >
                {tr.packageStatus
                  ? `${toTileCase(tr.packageStatus?.replace('_', ' '))} ${
                      tr.timeDifference
                        ? `by ${formatMs(tr.timeDifference)}`
                        : ''
                    }`
                  : ''}
              </ColorSpan>
              <Span mb={1} color="#727272">
                {`${t('Target')} - ${formatMs(Number(tr.targetTime))}`}
              </Span>
              <Span mb={1} color="#727272">
                {`${t('Actual')} - ${formatMs(Number(tr.actualTransitTime))}`}
              </Span>
            </ColFullHeight>
          )
        }
      }
    case 'exception':
      return {
        Header: () => <Header data-id={'exceptionCount'}>{t(label)}</Header>,
        id: key,
        width: 140,
        sortable: false,
        ...columnConfig,
        Cell: ({ row: { original: tr } }: any) => (
          <ColFullHeight>
            <Span color="red" variant="h5">
              {tr.exceptionCount}
            </Span>
          </ColFullHeight>
        )
      }
    case 'parcelStatus':
      return {
        Header: () => (
          <Header data-id="packageStatus,exceptionCount">{label}</Header>
        ),
        id: key,
        sortable: false,
        width: 110,
        ...columnConfig,
        Cell: ({ row: { original: tr } }: any) => {
          return (
            <ColFullHeight alignItemsFlexStart>
              <BgSpan
                variant="h6 ellipsis"
                style={{ padding: '4px 12px', borderRadius: 4 }}
                mb={1}
                title={tr?.packageStatus?.replace(' ', '') || ''}
                color={'#fff'}
              >
                {startCase(tr.packageStatus) || '-'}
              </BgSpan>
              {
                <Span mb={1} style={{ fontSize: '12px', color: '#727272' }}>
                  Exceptions: <BoldText>{tr.exceptionCount || 0}</BoldText>
                </Span>
              }
              {/* <Span mb={1} variant="secondary">
                  Delays: <BoldText>{tr.delaysCount || '-'}</BoldText>
                </Span> */}
            </ColFullHeight>
          )
        }
      }
    case 'parcelEtaSta':
      return {
        Header: () => <Header data-id={'eta,sta'}>{label}</Header>,
        id: key,
        width: 140,
        sortable: false,
        ...columnConfig,
        Cell: ({ row: { original: tr } }: any) => (
          <ColFullHeight>
            <BoldText variant="ellipsis h6" mb={1}>
              E.T.A {tr?.eta?.time ? displayTime(tr.eta) : displayTime(tr?.sta)}
            </BoldText>
            <Span variant="ellipsis">
              S.T.A {tr?.sta ? displayTime(tr?.sta) : '-'}
            </Span>
          </ColFullHeight>
        )
      }
    case 'parcelCarrier':
      return {
        Header: () => (
          <Header data-id={'carrierName,carrierCode'}>{t(label)}</Header>
        ),
        id: key,
        width: 130,
        sortable: false,
        ...columnConfig,
        Cell: ({ row: { original: tr } }: any) => {
          return (
            <ColFullHeight>
              <Span
                style={{
                  fontSize: '14px',
                  lineHeight: '16px',
                  textTransform: 'uppercase',
                  color: '#006491',
                  top: 'calc(50% - 16px/2 - 9.5px)'
                }}
                data-id="carrierName"
                variant="ellipsis"
              >
                {t(tr?.carrierName)}
              </Span>
              <Flex column>
                <Flex>
                  <Span
                    style={{
                      color: '#727272',
                      right: '1.89%',
                      top: 'calc(50% - 14px/2 + 10.5px)'
                    }}
                    data-id="typeOfService"
                    variant="ellipsis"
                  >
                    {toTileCase(t(tr?.typeOfService)) || '-'}
                  </Span>
                </Flex>
              </Flex>
            </ColFullHeight>
          )
        }
      }
    case 'parcelConsignor':
      return {
        Header: () => <Header data-id={'consignerName,origin'}>{label}</Header>,
        id: key,
        width: 110,
        sortable: false,
        ...columnConfig,
        Cell: ({ row: { original: tr } }: any) => (
          <ColFullHeight>
            <BoldText variant="ellipsis h6" mb={1}>
              {toTileCase(tr.consignerName) || '-'}
            </BoldText>
            <Span variant="ellipsis">
              {toTileCase(tr?.consignerCode || tr?.origin) || '-'}
            </Span>
          </ColFullHeight>
        )
      }
    case 'parcelConsignee':
      return {
        Header: () => (
          <Header data-id={'consigneeName,destination'}>{label}</Header>
        ),
        id: key,
        width: 110,
        sortable: false,
        ...columnConfig,
        Cell: ({ row: { original: tr } }: any) => (
          <ColFullHeight>
            <BoldText variant="ellipsis h6" mb={1}>
              {tr.consigneeName || '-'}
            </BoldText>
            <Span variant="ellipsis">
              {toTileCase(tr?.consigneeCode || tr?.destination) || '-'}
            </Span>
          </ColFullHeight>
        )
      }
    case 'parcelItemCount':
      return {
        Header: () => <Header data-id={'itemCount'}>{label}</Header>,
        id: key,
        width: 50,
        sortable: false,
        ...columnConfig,
        Cell: ({ row: { original: tr } }: any) => (
          <ColFullHeight>
            <BoldText>{tr.itemCount || '-'}</BoldText>
          </ColFullHeight>
        )
      }
    case 'parcelOrderNo':
      return {
        Header: () => <Header data-id={'orderNo'}>{label}</Header>,
        id: key,
        width: 135,
        sortable: false,
        ...columnConfig,
        Cell: ({ row: { original: tr } }: any) => (
          <ColFullHeight>
            <BoldText>{tr.orderNo || '-'}</BoldText>
          </ColFullHeight>
        )
      }

    case 'packageStatus':
      return {
        Header: () => <Header data-id={'packageStatus'}>{t(label)}</Header>,
        id: key,
        sortable: false,
        sticky: 'right',
        width: 135,
        ...columnConfig,
        Cell: ({ row: { original } }: any) => {
          return (
            <ColFullHeight alignItemsCenter>
              <BgSpan
                title={original?.packageStatus?.replace(' ', '')}
                variant="h6"
                style={{ padding: '3px 5px', borderRadius: '6px' }}
                color={'#ffff'}
              >
                {t(startCase(original.packageStatus as string)) || '-'}
              </BgSpan>
            </ColFullHeight>
          )
        }
      }
    case 'milestoneRawStatus':
      return {
        Header: () => <Header data-id={key}>{t(label)}</Header>,
        id: key,
        width: 120,
        ...columnConfig,
        Cell: ({ row: { original } }: any) => {
          return (
            <ColFullHeight style={{ display: 'inline-flex' }}>
              {original[key] ? (
                <Span
                  style={{
                    background: '#FBEFC3',
                    padding: '3px 4px',
                    borderRadius: 4
                  }}
                  variant="ellipsis"
                >
                  {t(original[key])}
                </Span>
              ) : (
                <Span>-</Span>
              )}
            </ColFullHeight>
          )
        }
      }
    default:
      return {
        Header: () => <Header data-id={key}>{t(label)}</Header>,
        id: key,
        width: 140,
        ...columnConfig,
        Cell: ({ row: { original } }: any) => {
          let value = getValueFromNestedKey(original, key)
          return (
            <ColFullHeight>
              <Span variant="ellipsis">
                {formatter && formatters[formatter]
                  ? formatters[formatter](value) || '-'
                  : value
                  ? t(value)
                  : value === 0
                  ? 0
                  : '-'}
              </Span>
            </ColFullHeight>
          )
        }
      }
  }
}
export function getTableColumns(
  reqdColumns: IColumn[],
  { links, queryParams = {}, t }: any
) {
  const result = reqdColumns.map(ele =>
    generateColumnObject(ele, { t, links, queryParams })
  )
  return result
}
